import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import toString from 'lodash/toString';

type AdditionalContext = Record<string, unknown>;

const TYPE_LABELS: Record<string, string> = {
  pod_anti_affinity: 'Anti-affinities',
  pod_disruption_budget: 'Problematic PDBs',
  missing_probes: 'Missing health checks',
  node_selectors: 'Unsupported node selectors',
  resource_quota_limit: 'Resource quotas',
  topology_spread_constraint: 'Topology spreads',
};

const REASON_LABELS: Record<string, string> = {
  missing_liveness_probe: 'Liveness probe missing',
  missing_readiness_probe: 'Readiness probe missing',
  too_strict_pdb: 'PDB too strict',
  lack_of_pdb: 'Missing PDB',
  missing_unavailable_setting: 'Missing unavailable setting',
  too_high_skew: 'Skew too high',
  missing_topology_spread_constraints: 'Missing topology spread',
  unsupported_node_selector: 'Unsupported node selector',
  strict_pod_anti_affinity: 'Required anti-affinity',
  preferred_pod_anti_affinity: 'Preferred anti-affinity',
};

const DEFAULT_DESCRIPTIONS: Record<string, string> = {
  missing_readiness_probe: '',
  missing_liveness_probe: '',
  too_strict_pdb: 'PDB configuration is too strict and may block rollouts.',
  strict_pod_anti_affinity: 'Pods cannot share a node.',
  preferred_pod_anti_affinity: 'Slightly reduces scheduling flexibility.',
  unsupported_node_selector: 'No matching nodes',
};

export const formatRestrictionType = (type: string): string => {
  return TYPE_LABELS[type] || type;
};

const formatContextValue = (value: unknown): string | undefined => {
  if (isString(value)) {
    return value;
  }
  if (isNumber(value) || isBoolean(value)) {
    return String(value);
  }
  if (value === null || value === undefined) {
    return undefined;
  }
  if (isPlainObject(value) || Array.isArray(value)) {
    try {
      return JSON.stringify(value);
    } catch {
      return toString(value);
    }
  }
  return toString(value);
};

const getContextString = (
  additionalContext: AdditionalContext | undefined,
  key: string
) => formatContextValue(additionalContext?.[key]);

export const formatIssueReason = (
  reason: string,
  additionalContext?: AdditionalContext
): string => {
  // For PDB issues, format with minAvailable if available
  const minAvailable = getContextString(additionalContext, 'minAvailable');
  if (reason === 'too_strict_pdb' && minAvailable) {
    return `minAvailable: ${minAvailable}`;
  }

  // For node selector issues, use the selector key from context
  const selector = getContextString(additionalContext, 'selector');
  if (reason === 'unsupported_node_selector' && selector) {
    return selector;
  }

  // For topology spread issues, use topologyKey from context
  const topologyKey = getContextString(additionalContext, 'topologyKey');
  if (reason === 'missing_topology_spread_constraints' && topologyKey) {
    return `topologyKey ${topologyKey}`;
  }

  // For resource quota issues, format with namespace and resource details
  if (reason === 'resource_quota_limit' && additionalContext) {
    const namespace = getContextString(additionalContext, 'namespace');
    const resource = getContextString(additionalContext, 'resource');
    const used = getContextString(additionalContext, 'used');
    const limit = getContextString(additionalContext, 'limit');
    if (resource && used && limit) {
      return `${
        namespace ? `namespace: ${namespace}` : ''
      } ${resource} — ${used} / ${limit} used`.trim();
    }
  }

  // For limit range issues, format with namespace and resource details
  if (reason === 'limit_range' && additionalContext) {
    const namespace = getContextString(additionalContext, 'namespace');
    const resource = getContextString(additionalContext, 'resource');
    const min = getContextString(additionalContext, 'min');
    const max = getContextString(additionalContext, 'max');
    if (resource && min) {
      const range = max ? `min=${min}, max=${max}` : `min=${min}`;
      return `${
        namespace ? `namespace: ${namespace}` : ''
      } ${resource} — ${range}`.trim();
    }
  }

  // Default reason mapping
  return REASON_LABELS[reason] || reason;
};

const getContextDescription = (
  context: AdditionalContext,
  fields: string[],
  fallback = ''
) => {
  return (
    fields.map((field) => getContextString(context, field)).find(Boolean) ||
    fallback
  );
};

const DESCRIPTION_BUILDERS: Record<
  string,
  (context: AdditionalContext) => string
> = {
  pod_disruption_budget: (context) => {
    const currentValue = getContextString(context, 'currentValue');
    const recommendedValue = getContextString(context, 'recommendedValue');

    if (currentValue && recommendedValue) {
      return `PDB minAvailable is too strict (current: ${currentValue}, recommended: ${recommendedValue})`;
    }

    return getContextDescription(context, ['description', 'reason']);
  },
  node_selectors: (context) =>
    getContextDescription(context, ['reason', 'description']),
  resource_quota_limit: (context) =>
    getContextDescription(context, ['status', 'description']),
  limit_range: (context) =>
    getContextDescription(
      context,
      ['status', 'description'],
      'Limit too restrictive'
    ),
  topology_spread_constraint: (context) =>
    getContextDescription(context, ['description', 'reason']),
  pod_anti_affinity: (context) =>
    getContextDescription(context, ['description', 'reason']),
  missing_probes: (context) => {
    const container = getContextString(context, 'container');
    if (container) {
      return `Container: ${container}`;
    }
    return getContextString(context, 'description') || '';
  },
};

export const formatIssueDescription = (
  issue: {
    reason?: string;
    additionalContext?: AdditionalContext;
  },
  restrictionType?: string
): string => {
  if (
    issue.reason === 'missing_topology_spread_constraints' &&
    issue.additionalContext
  ) {
    return getContextString(issue.additionalContext, 'description') || '';
  }

  // For too_high_skew, format the description with max and current skew
  if (issue.reason === 'too_high_skew' && issue.additionalContext) {
    const maxSkew = getContextString(issue.additionalContext, 'maxSkew');
    const recommendedMax = getContextString(
      issue.additionalContext,
      'recommendedMax'
    );
    if (maxSkew && recommendedMax) {
      return `Max skew allowed ${recommendedMax}, current skew ${maxSkew}`;
    }
  }

  // Use additionalContext to build detailed descriptions
  if (issue.additionalContext) {
    if (restrictionType) {
      const descriptionBuilder = DESCRIPTION_BUILDERS[restrictionType];
      const formattedDescription = descriptionBuilder?.(
        issue.additionalContext
      );
      if (formattedDescription) {
        return formattedDescription;
      }

      // If we have additional context for PDB but couldn't build
      if (restrictionType === 'pod_disruption_budget') {
        return DEFAULT_DESCRIPTIONS[issue.reason || ''] || '';
      }
    }

    // Fallback: join all context values
    return (
      Object.entries(issue.additionalContext)
        .filter(([key]) => key !== 'namespace' && key !== 'resource')
        .map(([, value]) => formatContextValue(value))
        .filter(Boolean)
        .join(', ') || ''
    );
  }

  // Default descriptions based on reason
  return DEFAULT_DESCRIPTIONS[issue.reason || ''] || '';
};
